import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { api } from '../../lib/api';
import { categoryLabel, countryLabel, regimeLabel } from '../../lib/labels';
import type { Site } from '../../lib/types';
import { Badge, Banner, Button, Card, Icon, LoadError, Skeleton, input } from '../../lib/ui';
import { Hilfe } from '../help/Help';
import { fmtDateTime, t } from '../../lib/i18n';

/**
 * Producing the evidence (Art. 7 (1) GDPR, Doc 03 §5).
 *
 * The proofs were written and never read: the product collected exactly what a data
 * subject request or a supervisory authority asks for, and the only way to get it out was
 * a clickhouse-client invocation — which is literally what the demo page documents.
 *
 * What makes a row evidence rather than a log line is the second half of this screen: the
 * wording that stood above the button when the person clicked it, recovered through the
 * config_version stored on every proof.
 */
interface Decision {
  consent_id: string;
  decision_seq: number;
  server_ts: number;
  action: string;
  source: string;
  regime: string;
  country: string;
  lang: string;
  page_host: string;
  page_path: string;
  categories: Record<string, boolean>;
  config_version: string;
  policy_version: number;
  text_hash: string;
  subject_hash: string;
  chain_seq: number;
  row_hash: string;
  shown?: Record<string, string>;
}

interface ChainCheck {
  from: number;
  to: number;
  checked: number;
  intact: boolean;
  broken_at?: number;
  reason?: string;
  checked_at: string;
}

interface ProofResp {
  query: string;
  decisions: Decision[];
  chain?: ChainCheck;
}

// German values are keys; translated where the row renders.
const ACTION: Record<string, string> = {
  accept_all: 'Alles akzeptiert',
  reject_all: 'Alles abgelehnt',
  save: 'Auswahl gespeichert',
  withdraw: 'Widerrufen',
};

export function ProofsScreen({ site }: { site: Site }) {
  const [input_, setInput] = useState('');
  const [q, setQ] = useState('');

  const proofs = useQuery({
    queryKey: ['proofs', site.id, q],
    queryFn: () => api<ProofResp>(`/v1/sites/${site.id}/proofs?q=${encodeURIComponent(q)}`),
    enabled: q !== '',
  });

  const download = () => {
    const blob = new Blob([JSON.stringify(proofs.data, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `nachweis-${q}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const d = proofs.data;

  return (
    <div className="space-y-4">
      <Card title={t('Nachweis suchen')} actions={<Hilfe to="nachweise" />}>
        <p className="mb-3 text-[13px] text-[var(--color-muted)]">
          {t('Eine Einwilligungs-ID oder ein Subjekt-Hash. Die ID kann im Auswahl-Dialog angezeigt werden — dann bringt eine Auskunftsanfrage sie meist gleich mit.')}
        </p>
        <form
          className="flex flex-wrap gap-2"
          onSubmit={(e) => { e.preventDefault(); setQ(input_.trim()); }}
        >
          <input className={`${input} max-w-[420px] font-mono`} value={input_}
            placeholder="01KZQRCW275FDPC7JVJ92HR1A8" aria-label={t('Einwilligungs-ID oder Subjekt-Hash')}
            onChange={(e) => setInput(e.target.value)} />
          <Button type="submit" disabled={!input_.trim()}>
            <Icon name="search" /> {t('Suchen')}
          </Button>
        </form>
      </Card>

      {proofs.isPending && q !== '' && <Skeleton rows={4} />}
      {proofs.isError && <LoadError what={t('Der Nachweis')} onRetry={() => void proofs.refetch()} />}

      {d && d.decisions.length === 0 && (
        <Banner kind="info">
          {t('Zu {query} gibt es auf dieser Website keinen Beleg. Gehört die ID zu einer anderen Website dieses Kontos, suchen Sie dort — Belege sind pro Website getrennt.', { query: d.query })}
        </Banner>
      )}

      {d && d.decisions.length > 0 && (
        <>
          {d.chain && <ChainCard check={d.chain} />}

          <Card
            title={t('Entscheidungen')}
            count={d.decisions.length}
            actions={<Button variant="secondary" size="sm" onClick={download}>{t('Als JSON sichern')}</Button>}
          >
            <ul className="space-y-4">
              {d.decisions.map((x) => <DecisionRow key={`${x.consent_id}:${x.decision_seq}`} d={x} />)}
            </ul>
          </Card>
        </>
      )}
    </div>
  );
}

/**
 * The tamper-evidence verdict, said as precisely as it can honestly be said.
 *
 * ClickHouse cannot prevent a delete, so the claim is never "this cannot be changed" but
 * "a change would be visible here" — and when the chain does break, the reason matters:
 * a missing proof and two chains for one site are different accusations.
 */
function ChainCard({ check }: { check: ChainCheck }) {
  return (
    <Card title={t('Unversehrtheit')} actions={<Hilfe to="unversehrtheit" />}>
      {check.intact ? (
        <Banner kind="ok">
          <span>
            {t('Die Belege {from}–{to} folgen lückenlos auseinander ({checked} geprüft). Jede nachträgliche Änderung an einem davon würde hier auffallen.',
              { from: check.from, to: check.to, checked: check.checked })}
          </span>
        </Banner>
      ) : (
        <Banner kind="error">
          <span>
            {t('Die Kette bricht bei Beleg {at}: {reason}. Das heißt nicht, dass dieser Nachweis falsch ist — es heißt, dass die Reihe um ihn herum nicht mehr geschlossen ist und der Grund geklärt werden sollte.',
              { at: check.broken_at ?? '?', reason: check.reason ?? '' })}
          </span>
        </Banner>
      )}
      <p className="mt-2 text-xs text-[var(--color-muted)]">
        {t('Geprüft wird durch Neuberechnung derselben Prüfsummen, die beim Schreiben entstanden sind — mit demselben Code, nicht mit einer zweiten Umsetzung davon.')}
      </p>
    </Card>
  );
}

function DecisionRow({ d }: { d: Decision }) {
  const allowed = Object.entries(d.categories).filter(([, on]) => on).map(([k]) => k);
  const denied = Object.entries(d.categories).filter(([, on]) => !on).map(([k]) => k);
  const text = d.shown ? Object.values(d.shown)[0] : undefined;

  return (
    <li className="rounded-[3px] border border-[var(--color-line)]">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1
                      border-b border-[var(--color-line)] bg-[var(--color-paper)] px-3 py-2">
        <span className="flex items-baseline gap-2">
          <strong className="text-[13px]">{ACTION[d.action] ? t(ACTION[d.action]!) : d.action}</strong>
          <span className="text-xs text-[var(--color-muted)]">
            {fmtDateTime(d.server_ts)}
          </span>
        </span>
        <span className="text-xs text-[var(--color-muted)]">
          {d.decision_seq === 0 ? t('Erste Entscheidung') : t('Änderung {n}', { n: d.decision_seq })}
          {' · '}{regimeLabel(d.regime)}{' · '}{countryLabel(d.country)}
        </span>
      </div>

      <div className="space-y-3 px-3 py-3">
        <div className="flex flex-wrap gap-1.5">
          {allowed.map((c) => (
            <Badge key={c} tone="live">{t('{category}: erlaubt', { category: categoryLabel(c) })}</Badge>
          ))}
          {denied.map((c) => (
            <Badge key={c}>{t('{category}: abgelehnt', { category: categoryLabel(c) })}</Badge>
          ))}
        </div>

        {/* The point of the whole screen. A record saying "analytics: yes" is a click; the
            wording that stood above the button is what Art. 7 (1) actually asks for. */}
        {text ? (
          <div>
            <div className="eyebrow mb-1">{t('Was gezeigt wurde')}</div>
            <blockquote className="whitespace-pre-line border-l-2 border-[var(--color-line)]
                                   pl-3 text-[13px] text-[var(--color-ink)]">
              {text}
            </blockquote>
          </div>
        ) : (
          <p className="text-xs text-[var(--color-muted)]">
            {t('Der Wortlaut dieser Fassung ({version}) liegt nicht mehr vor. Die Entscheidung selbst bleibt gültig; die Prüfsumme des Textes ist festgehalten.', { version: d.config_version })}
          </p>
        )}

        <dl className="grid gap-x-6 gap-y-1 text-xs text-[var(--color-muted)] sm:grid-cols-2">
          <Row k={t('Seite')} v={`${d.page_host}${d.page_path}`} />
          <Row k={t('Sprache')} v={d.lang} />
          <Row k={t('Fassung')}
            v={t('{version} · Regelwerk {policy}', { version: d.config_version, policy: d.policy_version })} />
          <Row k={t('Textprüfsumme')} v={d.text_hash} />
          <Row k={t('Subjekt')} v={d.subject_hash} />
          <Row k={t('Beleg')} v={`${d.chain_seq} · ${d.row_hash.slice(0, 16)}…`} />
        </dl>
      </div>
    </li>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex gap-2">
      <dt className="shrink-0">{k}</dt>
      <dd className="truncate font-mono" title={v}>{v}</dd>
    </div>
  );
}
